import { useState, useRef, useCallback, useEffect } from 'react'
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, setHours, setMinutes, isToday } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { cn } from '@/lib/utils'
import { TimeEntry } from '@/types'
import { Skeleton } from '@/components/ui/skeleton'
import { useAgencyTheme } from '@/contexts/AgencyThemeContext'
import { toAgencyTimezone, fromAgencyTimezone, getTimezoneAbbreviation } from '@/utils/timezone'
import { Globe } from 'lucide-react'

interface TimeCalendarViewProps {
  entries: TimeEntry[]
  viewMode: 'week' | 'month'
  currentDate: Date
  onSlotSelect: (start: Date, end: Date) => void
  onEntryClick: (entry: TimeEntry) => void
  isLoading?: boolean
}

const HOUR_HEIGHT = 60 // pixels per hour
const START_HOUR = 0 // 12 AM (midnight)
const END_HOUR = 24 // 12 AM (next day)
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i)

export default function TimeCalendarView({
  entries,
  viewMode,
  currentDate,
  onSlotSelect,
  onEntryClick,
  isLoading,
}: TimeCalendarViewProps) {
  const { timezone } = useAgencyTheme()
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState<{ day: Date; hour: number; y: number } | null>(null)
  const [dragEnd, setDragEnd] = useState<{ hour: number; minutes: number } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const hasScrolledRef = useRef(false)

  // Scroll to current hour on mount (centered in view)
  useEffect(() => {
    if (hasScrolledRef.current || !scrollContainerRef.current) return

    // Get current hour in agency timezone
    const now = new Date()
    const currentHourInAgencyTz = parseInt(formatInTimeZone(now, timezone, 'H'), 10)

    // Calculate scroll position to center the current hour
    const containerHeight = scrollContainerRef.current.clientHeight
    const headerHeight = 60 // Approximate height of the sticky header
    const currentHourOffset = (currentHourInAgencyTz - START_HOUR) * HOUR_HEIGHT

    // Center the current hour in the visible area
    const scrollPosition = currentHourOffset - (containerHeight - headerHeight) / 2 + HOUR_HEIGHT / 2

    // Ensure we don't scroll to negative values
    scrollContainerRef.current.scrollTop = Math.max(0, scrollPosition)
    hasScrolledRef.current = true
  }, [timezone])

  // Get days for the current view
  const getDays = () => {
    if (viewMode === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 })
      const end = endOfWeek(currentDate, { weekStartsOn: 1 })
      return eachDayOfInterval({ start, end })
    }
    const start = startOfMonth(currentDate)
    const end = endOfMonth(currentDate)
    return eachDayOfInterval({ start, end })
  }

  const days = getDays()

  // Get entries for a specific day (using agency timezone)
  const getEntriesForDay = (day: Date) => {
    return entries.filter((entry) => {
      // Convert UTC time to agency timezone for comparison
      const entryDate = toAgencyTimezone(entry.start_time, timezone)
      return isSameDay(entryDate, day)
    })
  }

  // Calculate entry position and height (using agency timezone)
  const getEntryStyle = (entry: TimeEntry) => {
    // Convert UTC times to agency timezone for display
    const start = toAgencyTimezone(entry.start_time, timezone)
    const end = entry.end_time ? toAgencyTimezone(entry.end_time, timezone) : new Date()
    const startHour = start.getHours() + start.getMinutes() / 60
    const endHour = end.getHours() + end.getMinutes() / 60
    const top = (startHour - START_HOUR) * HOUR_HEIGHT
    const height = Math.max((endHour - startHour) * HOUR_HEIGHT, 20)
    return { top: `${top}px`, height: `${height}px` }
  }

  // Handle mouse down for drag selection
  const handleMouseDown = (e: React.MouseEvent, day: Date, hour: number) => {
    if (e.button !== 0) return // Only left click
    const rect = e.currentTarget.getBoundingClientRect()
    const y = e.clientY - rect.top
    setIsDragging(true)
    setDragStart({ day, hour, y })
    setDragEnd({ hour, minutes: Math.floor((y / HOUR_HEIGHT) * 60) % 60 })
  }

  // Handle mouse move during drag
  const handleMouseMove = useCallback((e: React.MouseEvent, hour: number) => {
    if (!isDragging || !dragStart) return
    const rect = e.currentTarget.getBoundingClientRect()
    const y = e.clientY - rect.top
    const minutes = Math.floor((y / HOUR_HEIGHT) * 60) % 60
    setDragEnd({ hour, minutes })
  }, [isDragging, dragStart])

  // Handle mouse up to complete selection
  const handleMouseUp = () => {
    if (isDragging && dragStart && dragEnd) {
      // Create dates representing the selected time in agency timezone
      const startDateLocal = setMinutes(setHours(dragStart.day, dragStart.hour), 0)
      const endHour = dragEnd.hour + (dragEnd.minutes > 30 ? 1 : 0.5)
      const endDateLocal = setMinutes(setHours(dragStart.day, Math.floor(endHour)), (endHour % 1) * 60)

      // Convert from agency timezone to UTC for storage
      // The calendar displays times in agency timezone, so the user's selection is in agency timezone
      const startDate = fromAgencyTimezone(startDateLocal, timezone)
      const endDate = fromAgencyTimezone(endDateLocal, timezone)

      if (endDate > startDate) {
        onSlotSelect(startDate, endDate)
      }
    }
    setIsDragging(false)
    setDragStart(null)
    setDragEnd(null)
  }

  // Get drag selection style
  const getDragStyle = (day: Date, hour: number) => {
    if (!isDragging || !dragStart || !dragEnd || !isSameDay(day, dragStart.day)) return null
    const startHour = Math.min(dragStart.hour, dragEnd.hour)
    const endHour = Math.max(dragStart.hour, dragEnd.hour) + 1
    if (hour < startHour || hour >= endHour) return null
    return 'bg-primary/20 border-primary border-2 border-dashed'
  }

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    )
  }

  return (
    <div
      className="overflow-auto h-[calc(100vh-280px)] min-h-[400px]"
      ref={scrollContainerRef}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div className="min-w-[800px]" ref={containerRef}>
        {/* Header with day names */}
        <div className="flex border-b sticky top-0 bg-background z-10">
          {/* Timezone indicator in the corner */}
          <div className="w-16 flex-shrink-0 border-r flex items-center justify-center">
            <div className="flex items-center gap-1 text-xs text-muted-foreground" title={`Timezone: ${timezone}`}>
              <Globe className="h-3 w-3" />
              <span>{getTimezoneAbbreviation(timezone)}</span>
            </div>
          </div>
          {days.map((day) => (
            <div
              key={day.toISOString()}
              className={cn(
                'flex-1 text-center py-3 border-r',
                isToday(day) && 'bg-primary/5'
              )}
            >
              <div className="text-sm text-muted-foreground">{format(day, 'EEE')}</div>
              <div className={cn(
                'text-lg font-semibold',
                isToday(day) && 'text-primary'
              )}>
                {format(day, 'd')}
              </div>
            </div>
          ))}
        </div>

        {/* Time grid */}
        <div className="flex">
          {/* Hour labels */}
          <div className="w-16 flex-shrink-0 border-r">
            {HOURS.map((hour) => (
              <div key={hour} className="h-[60px] text-xs text-muted-foreground text-right pr-2 pt-1">
                {format(setHours(new Date(), hour), 'h a')}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((day) => (
            <div
              key={day.toISOString()}
              className={cn('flex-1 border-r relative', isToday(day) && 'bg-primary/5')}
            >
              {/* Hour slots */}
              {HOURS.map((hour) => (
                <div
                  key={hour}
                  className={cn(
                    'h-[60px] border-b cursor-pointer hover:bg-muted/50 transition-colors',
                    getDragStyle(day, hour)
                  )}
                  onMouseDown={(e) => handleMouseDown(e, day, hour)}
                  onMouseMove={(e) => handleMouseMove(e, hour)}
                />
              ))}

              {/* Entries */}
              {getEntriesForDay(day).map((entry) => (
                <div
                  key={entry.entry_id}
                  className={cn(
                    'absolute left-1 right-1 rounded-md px-2 py-1 cursor-pointer overflow-hidden',
                    'text-xs text-white shadow-sm hover:shadow-md transition-shadow',
                    entry.is_running && 'animate-pulse'
                  )}
                  style={{
                    ...getEntryStyle(entry),
                    backgroundColor: entry.project_color || '#3B82F6',
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    onEntryClick(entry)
                  }}
                >
                  <div className="font-medium truncate">{entry.description}</div>
                  {entry.project_name && (
                    <div className="truncate opacity-80">{entry.project_name}</div>
                  )}
                  {entry.duration_minutes && (
                    <div className="opacity-80">
                      {Math.floor(entry.duration_minutes / 60)}h {entry.duration_minutes % 60}m
                    </div>
                  )}
                </div>
              ))}

              {/* Current time indicator (only for today) */}
              {isToday(day) && (() => {
                const now = new Date()
                const currentHour = parseInt(formatInTimeZone(now, timezone, 'H'), 10)
                const currentMinute = parseInt(formatInTimeZone(now, timezone, 'm'), 10)
                const topPosition = (currentHour - START_HOUR) * HOUR_HEIGHT + (currentMinute / 60) * HOUR_HEIGHT
                return (
                  <div
                    className="absolute left-0 right-0 z-20 pointer-events-none"
                    style={{ top: `${topPosition}px` }}
                  >
                    <div className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-red-500 -ml-1" />
                      <div className="flex-1 h-0.5 bg-red-500" />
                    </div>
                  </div>
                )
              })()}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
